import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { Search, Filter, Truck, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { orders, type AdminOrder, type OrderStatus } from '../api/endpoints'
import { Button } from '../components/Button'
import { DataTable } from '../components/DataTable'
import { Input, Select } from '../components/Input'
import { StatusBadge } from '../components/StatusBadge'
import { formatINR, timeAgo } from '../lib/format'
import { clampInt, parseIntParam } from '../lib/query'

const STATUS_OPTIONS: Array<{ value: OrderStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'created', label: 'Created' },
  { value: 'paid', label: 'Paid' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
]

export function OrdersPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<AdminOrder[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [status, setStatus] = useState<OrderStatus | ''>('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    orders
      .list({ status: status || undefined, search: search || undefined, page, limit })
      .then((r) => {
        if (!cancelled) {
          setData(r.orders)
          setTotal(r.total)
        }
      })
      .catch((err) => toast.error('Failed to load orders', { description: err.message }))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [page, status, search, limit])

  const columns: ColumnDef<AdminOrder>[] = [
    {
      header: 'Order',
      accessorKey: 'orderNumber',
      cell: ({ row }) => (
        <Link
          to={`/orders/${row.original.id}`}
          className="font-mono text-sm text-ink hover:text-forest"
        >
          {row.original.orderNumber}
        </Link>
      ),
    },
    {
      header: 'Customer',
      accessorFn: (r) => r.customer.name,
      cell: ({ row }) => (
        <div>
          <p className="text-sm text-ink">{row.original.customer.name}</p>
          <p className="text-xs text-slatey">{row.original.customer.phone}</p>
        </div>
      ),
    },
    {
      header: 'Items',
      accessorKey: 'itemCount',
      cell: ({ getValue }) => <span className="text-sm">{String(getValue())}</span>,
    },
    {
      header: 'Amount',
      accessorKey: 'amount',
      cell: ({ getValue }) => (
        <span className="text-sm font-medium">{formatINR(getValue<number>())}</span>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }) => <StatusBadge status={getValue<OrderStatus>()} />,
    },
    {
      header: 'When',
      accessorKey: 'createdAt',
      cell: ({ getValue }) => (
        <span className="text-xs text-slatey">{timeAgo(getValue<string>())}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const o = row.original
        // Manifest is only meaningful for paid orders. Server rejects
        // other statuses (409); we hide the button instead of letting
        // the admin click into a guaranteed failure.
        if (o.status !== 'paid') return null
        return (
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (!confirm(`Manifest order ${o.orderNumber}? This marks it as delivered.`)) return
              orders
                .manifest(o.id)
                .then((r) => {
                  toast.success('Order marked as delivered')
                  // Update the row in place — refresh the list count too
                  setData((prev) =>
                    prev.map((x) => (x.id === o.id ? r.order : x)),
                  )
                })
                .catch((err) =>
                  toast.error('Manifest failed', { description: err.message }),
                )
            }}
            className="size-7 rounded text-slatey hover:text-forest hover:bg-forest/10 inline-flex items-center justify-center"
            title="Manifest shipment"
          >
            <Truck className="size-3.5" />
          </button>
        )
      },
    },
  ]

  const totalPages = Math.max(1, Math.ceil(total / limit))
  const start = total === 0 ? 0 : (page - 1) * limit + 1
  const end = Math.min(page * limit, total)

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-ink">Orders</h2>
          <p className="text-xs text-slatey">{total} total</p>
        </div>
        <Button onClick={() => navigate('/orders/new')} size="sm">
          <Plus className="size-4" /> New order
        </Button>
      </div>
      <div className="card p-4 flex flex-wrap items-center gap-3">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setSearch(searchInput.trim())
            setPage(1)
          }}
          className="flex items-center gap-2 flex-1 min-w-[260px]"
        >
          <div className="relative flex-1">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-slatey" />
            <Input
              placeholder="Search by order #, customer name, or phone"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            Search
          </Button>
        </form>
        <div className="flex items-center gap-2">
          <Filter className="size-4 text-slatey" />
          <Select
            value={status}
            onChange={(e) => {
              setStatus((e.target.value || '') as OrderStatus | '')
              setPage(1)
            }}
            className="min-w-[160px]"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        onRowClick={(row) => navigate(`/orders/${row.id}`)}
        emptyTitle="No orders match"
        emptyDescription="Try clearing your filters or adjusting the search query."
      />

      {total > 0 ? (
        <div className="flex items-center justify-between text-xs text-slatey">
          <p>
            Showing <span className="text-ink font-medium">{start}–{end}</span> of{' '}
            <span className="text-ink font-medium">{total}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="font-mono">
              Page {parseIntParam(String(page), 1)} / {clampInt(String(totalPages), 1, 9999, 1)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}